import React, { useId } from 'react';
import { Box, Flex } from 'rebass';
import Icon from '../Icon/Icon';
import type { ActivityFeedItem } from './ActivityFeed.queries';
import ActivityActionTooltip, {
  getActivityActionTooltipId,
} from './ActivityActionTooltip';

export type ActivityActionButtonsProps = {
  notification: ActivityFeedItem;
  onMarkRead: (notificationId: string | number) => Promise<void>;
  onMarkUnread: (notificationId: string | number) => Promise<void>;
  onMarkAsFollowUp: (notificationId: string | number) => Promise<void>;
  onUnMarkAsFollowUp: (notificationId: string | number) => Promise<void>;
  onDismiss: (notificationId: string | number) => Promise<void>;
  onUndismiss?: (notificationId: string | number) => Promise<void>;
  onActionComplete?: () => void | Promise<void>;
};

const actionButtonSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  border: 'none',
  borderRadius: 4,
  bg: 'transparent',
  color: 'onNavbar',
  cursor: 'pointer',
  flexShrink: 0,
  '&:hover': {
    bg: 'utilBtnHover',
  },
  '&:disabled': {
    cursor: 'default',
    opacity: 0.35,
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary',
    outlineOffset: '2px',
  },
};

const ActivityActionButtons = ({
  notification,
  onMarkRead,
  onMarkUnread,
  onMarkAsFollowUp,
  onUnMarkAsFollowUp,
  onDismiss,
  onUndismiss,
  onActionComplete,
}: ActivityActionButtonsProps): JSX.Element => {
  const isUnread = !notification.read;
  const isDismissed = notification.dismissed;
  const isFollowUp = !!notification.followUp;
  const followUpTitle = isFollowUp
    ? 'Unmark as follow-up'
    : 'Mark as follow-up';
  const readTitle = isUnread ? 'Mark as read' : 'Mark as unread';
  const dismissTitle = isDismissed ? 'Restore' : 'Clear';
  const tooltipId = getActivityActionTooltipId(useId());
  const runAction = async (action: () => Promise<void>) => {
    await action();
    await onActionComplete?.();
  };

  return (
    <Flex
      sx={{
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '3px',
      }}
    >
      <Box
        as="button"
        type="button"
        aria-label={followUpTitle}
        data-tooltip-id={tooltipId}
        data-tooltip-content={followUpTitle}
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          void runAction(() =>
            isFollowUp
              ? onUnMarkAsFollowUp(notification.id)
              : onMarkAsFollowUp(notification.id)
          );
        }}
        sx={{
          ...actionButtonSx,
          color: isFollowUp ? 'statusOrange' : 'onNavbar',
        }}
      >
        <Icon icon={isFollowUp ? 'bookmark-fill' : 'bookmark'} size="14" />
      </Box>
      <Box
        as="button"
        type="button"
        aria-label={readTitle}
        data-tooltip-id={tooltipId}
        data-tooltip-content={readTitle}
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          void runAction(() =>
            isUnread
              ? onMarkRead(notification.id)
              : onMarkUnread(notification.id)
          );
        }}
        sx={actionButtonSx}
      >
        <Icon icon={isUnread ? 'mail-open' : 'mail'} size="14" />
      </Box>
      <Box
        as="button"
        type="button"
        aria-label={dismissTitle}
        data-tooltip-id={tooltipId}
        data-tooltip-content={dismissTitle}
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          void runAction(() =>
            isDismissed && onUndismiss
              ? onUndismiss(notification.id)
              : onDismiss(notification.id)
          );
        }}
        sx={actionButtonSx}
      >
        {isDismissed ? (
          <Icon icon="eye" size="14" />
        ) : (
          <Icon icon="x" size="22" style={{ transform: 'scale(1.45)' }} />
        )}
      </Box>
      <ActivityActionTooltip id={tooltipId} />
    </Flex>
  );
};

export default ActivityActionButtons;
